import { useState, useEffect, useCallback } from 'react';
import { getAllMaintenanceRecordsUnlimited } from '../services/maintenanceService';
import { MaintenanceStatus } from '../utils/types';

interface MaintenanceNotificationSummary {
  totalCount: number;
  pendingCount: number; // طلبات قيد الانتظار وقيد العمل
  completedCount: number; // طلبات تم الإصلاح (جاهزة للتسليم)
  hasNotifications: boolean;
}

interface UseMaintenanceNotificationsReturn {
  summary: MaintenanceNotificationSummary;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
}

export const useMaintenanceNotifications = (
  refreshInterval: number = 5 * 60 * 1000 // 5 دقائق افتراضياً
): UseMaintenanceNotificationsReturn => {
  const [summary, setSummary] = useState<MaintenanceNotificationSummary>({
    totalCount: 0,
    pendingCount: 0,
    completedCount: 0,
    hasNotifications: false
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const calculateNotifications = useCallback(async (): Promise<MaintenanceNotificationSummary> => {
    try {
      // جلب جميع سجلات الصيانة بدون حد أقصى
      const allRecords = await getAllMaintenanceRecordsUnlimited();

      let pendingCount = 0;
      let completedCount = 0;

      allRecords.forEach(record => {
        // عد الطلبات قيد الانتظار (تشمل قيد الانتظار وقيد العمل)
        if (record.status === MaintenanceStatus.PENDING || record.status === MaintenanceStatus.IN_PROGRESS) {
          pendingCount++;
        }

        // عد الطلبات المكتملة (جاهزة للتسليم)
        if (record.status === MaintenanceStatus.COMPLETED) {
          completedCount++;
        }
      });

      const totalCount = pendingCount + completedCount;

      return {
        totalCount,
        pendingCount,
        completedCount,
        hasNotifications: totalCount > 0
      };
    } catch (err) {
      console.error('Error calculating maintenance notifications:', err);
      throw new Error('فشل في حساب إشعارات الصيانة');
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newSummary = await calculateNotifications();
      setSummary(newSummary);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error refreshing maintenance notifications:', err);
      setError(err instanceof Error ? err.message : 'حدث خطأ غير متوقع');
    } finally {
      setLoading(false);
    }
  }, [calculateNotifications]);

  // تحديث البيانات عند التحميل الأول
  useEffect(() => {
    refresh();
  }, [refresh]);

  // تحديث البيانات بشكل دوري
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(refresh, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refresh, refreshInterval]);

  return {
    summary,
    loading,
    error,
    refresh,
    lastUpdated
  };
};
